import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ColumnTypeEnum } from '@y42/json-schema';
import { EChartsOption } from 'echarts';
import { DistributionModel } from '@y42/model';
import { NumberTypes } from '@y42/column-type';
import { InputObservable, connectState } from '@y42/angular/util';
import { BehaviorSubject, Observable, combineLatest, distinctUntilChanged, map, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CardinalityModel } from '../../../../../cardinality/cardinality.model';
import { DatasourceColumnUIModel } from '../../../../../../state';

// TODO: Extract to a separate file
enum AxisTypeEnum {
  Value = 'value',
  Category = 'category',
  Time = 'time',
  Log = 'log',
}

@UntilDestroy()
@Component({
  selector: 'y42-table-column-stats',
  templateUrl: './table-column-stats.component.html',
  styleUrls: ['./table-column-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableColumnStatsComponent {
  constructor() {
    combineLatest([this.activeCardinality$, this.activeHeaderColumn$])
      .pipe(
        map(
          ([activeCardinality, activeHeaderColumn]) =>
            +(activeCardinality?.distinct?.[activeHeaderColumn?.field_name || ''] || 0) > 100,
        ),
        tap((showDataZoom) => {
          this.showDataZoom$$.next(showDataZoom);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }
  @Input()
  @InputObservable()
  activeHeaderColumn?: DatasourceColumnUIModel | null;
  private readonly activeHeaderColumn$!: Observable<DatasourceColumnUIModel | null>;

  @Input()
  @InputObservable()
  activeCardinality?: CardinalityModel | null;
  private readonly activeCardinality$!: Observable<CardinalityModel | null>;

  readonly showDataZoom$$ = new BehaviorSubject(false);
  readonly ColumnTypeEnum = ColumnTypeEnum;

  readonly state = connectState(this, {
    activeHeaderColumnTypeIsNumber: this.activeHeaderColumn$.pipe(
      map((activeHeaderColumn) => activeHeaderColumn && NumberTypes.includes(activeHeaderColumn.data_type)),
    ),
    total: combineLatest([this.activeCardinality$, this.activeHeaderColumn$]).pipe(
      map(([activeCardinality, activeHeaderColumn]) => {
        return activeCardinality?.total?.[`null_count_${activeHeaderColumn?.field_name}`]
          ? (
              (activeCardinality?.total?.[`null_count_${activeHeaderColumn?.field_name}`] /
                (activeCardinality?.total?.row_count || 1)) *
              100
            ).toFixed(2)
          : 0;
      }),
    ),
    showDataZoom: this.showDataZoom$$,
    echartOption: this.showDataZoom$$.pipe(
      distinctUntilChanged(),
      map((showDataZoom) => this.setUpEcharts(showDataZoom)),
    ),
  });

  private setUpEcharts(showDataZoom: boolean) {
    const echartOption: EChartsOption = {
      tooltip: {
        confine: true,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
      },
      grid: [
        {
          top: '5%',
          left: '10',
          width: '90%',
          height: showDataZoom ? '75%' : '90%',
          containLabel: true,
        },
      ],
      xAxis: [
        {
          type: this.getEchartsType(),
          show: true,
          name: '',
          nameRotate: 0,
          nameGap: 10,
          nameLocation: 'middle',
          nameTextStyle: {
            color: '#a7a7a7',
            fontStyle: 'normal',
            fontSize: 12,
            fontWeight: 'normal',
            align: 'center',
            verticalAlign: 'middle',
            backgroundColor: 'transparent',
            fontFamily: 'Arial',
            borderColor: '#f4f4fb',
            borderWidth: 0,
            borderRadius: 0,
            padding: [2, 2, 2, 2],
          },
          axisLabel: {
            show: true,
            rotate: 0,
            margin: 14,
            color: '#a7a7a7',
            fontStyle: 'normal',
            fontSize: 12,
            fontWeight: 'normal',
            align: 'center',
            verticalAlign: 'middle',
            backgroundColor: 'transparent',
            fontFamily: 'Arial',
            borderColor: '#f4f4fb',
            borderWidth: 0,
            borderRadius: 0,
            padding: [2, 2, 2, 2],
          },
          axisLine: {
            show: true,
            symbol: 'none',
            lineStyle: {
              color: '#a7a7a7',
              type: 'solid',
              width: 1,
            },
          },
          splitLine: {
            show: false,
            interval: 3,
            lineStyle: {
              color: ['#e9e9f6'],
              width: 1,
              type: 'solid',
              opacity: 1,
            },
          },
          min:
            this.getEchartsType() === AxisTypeEnum.Value
              ? +this.activeCardinality?.total?.[`min_${this.activeHeaderColumn?.field_name}`] - 1
              : undefined,
          max:
            this.getEchartsType() === AxisTypeEnum.Value
              ? +this.activeCardinality?.total?.[`max_${this.activeHeaderColumn?.field_name}`] + 1
              : undefined,
          gridIndex: 0,
        },
      ],
      yAxis: [
        {
          type: 'value',
          show: true,
          name: '',
          nameRotate: 0,
          nameGap: 10,
          nameLocation: 'middle',
          nameTextStyle: {
            color: '#a7a7a7',
            fontStyle: 'normal',
            fontSize: 12,
            fontWeight: 'normal',
            align: 'center',
            verticalAlign: 'middle',
            backgroundColor: 'transparent',
            fontFamily: 'Arial',
            borderColor: '#f4f4fb',
            borderWidth: 0,
            borderRadius: 0,
            padding: [2, 2, 2, 2],
          },
          minInterval: 1,
          axisLabel: {
            show: true,
            rotate: 0,
            margin: 14,
            color: '#a7a7a7',
            fontStyle: 'normal',
            fontSize: 12,
            fontWeight: 'normal',
            align: 'right',
            verticalAlign: 'middle',
            backgroundColor: 'transparent',
            fontFamily: 'Arial',
            borderColor: '#f4f4fb',
            borderWidth: 0,
            borderRadius: 0,
            padding: [2, 2, 2, 2],
          },
          axisLine: {
            show: true,
            symbol: 'none',
            lineStyle: {
              color: '#a7a7a7',
              type: 'solid',
              width: 1,
            },
          },
          splitLine: {
            show: false,
            interval: 3,
            lineStyle: {
              color: ['#e9e9f6'],
              width: 1,
              type: 'solid',
              opacity: 1,
            },
          },
          max: undefined,
          gridIndex: 0,
        },
      ],
      dataZoom: [
        {
          show: showDataZoom,
          type: 'slider',
          start: 0,
          end: 100,
          fillerColor: 'rgba(231, 235, 240, 0.5)',
          handleStyle: {
            color: '#1b1b52',
          },
        },
      ],
      series: [
        {
          type: 'bar',
          barMaxWidth: 60,
          datasetIndex: 0,
          encode: {
            y: 'sample_size',
            x: 'value',
          },
          itemStyle: {
            color: '#e9e9f6',
          },
          name: 'Sample Size',
          label: {
            show: false,
            rotate: 0,
            position: undefined,
            align: 'center',
            verticalAlign: 'top',
          },
          yAxisIndex: 0,
          xAxisIndex: 0,
        },
      ],
      dataset: [
        {
          source: (this.activeCardinality?.top?.[this.activeHeaderColumn?.field_name || ''] || [])
            .filter((data) => data.value || data.value === 0 || data.value === false)
            .map((res: DistributionModel) => ({
              // FIXME HUNG
              // does this still work after echart upgrade to v5?
              value: res.value as string | number,
              sample_size: res.sample_size,
            })),
        },
      ],
    };

    console.log('this.echartOption dataset', echartOption.dataset);

    return echartOption;
  }

  private getEchartsType(): AxisTypeEnum {
    switch (this.activeHeaderColumn?.data_type) {
      case ColumnTypeEnum.Boolean:
      case ColumnTypeEnum.ArrayString:
      case ColumnTypeEnum.String:
        return AxisTypeEnum.Category;
      case ColumnTypeEnum.Numeric:
      case ColumnTypeEnum.Integer:
      case ColumnTypeEnum.Float:
        return AxisTypeEnum.Value;
      case ColumnTypeEnum.Timestamp:
      case ColumnTypeEnum.Datetime:
      case ColumnTypeEnum.Date:
        return AxisTypeEnum.Time;
      default:
        return AxisTypeEnum.Category;
    }
  }
}
